import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  CardHeader,
  Button,
  Table,
} from "reactstrap";
import { Download } from "lucide-react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import Swal from "sweetalert2";
import { getPayrolls, exportPayrollHistoryToExcel } from "../../components/Api/GmsPayroll";
import { getPayrollDetailHistory } from "../../components/Api/GmsPayrollDetailHistory";

const PayrollHistoryReportPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [payrollList, setPayrollList] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPayrollHistoryReport');

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPayrollList(),
        loadPayrollHistory()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollList = async () => {
    try {
      const response = await getPayrolls({
        payroll_Id: null,
        payroll_Date: null
      });

      // Filter only posted payrolls for the dropdown
      const postedPayrolls = Array.isArray(response) 
        ? response.filter(p => p.payroll_IsPosted === true)
        : [];

      const payrollOptions = postedPayrolls.map(p => ({
        value: p.payroll_Id,
        label: `${moment(p.payroll_Date).format('MMMM YYYY')} - ${moment(p.payroll_PostedDate).format('DD/MM/YYYY')}`
      }));

      setPayrollList(payrollOptions);
    } catch (error) {
      console.error('Error loading payroll list:', error);
      Swal.fire("Error", "Failed to load payroll list: " + error.message, "error");
    }
  };

  const loadPayrollHistory = async (payrollId = null) => {
    setLoading(true);
    try {
      // First, get posted payrolls
      const payrollResponse = await getPayrolls({
        payroll_Id: payrollId,
        payroll_Date: null
      });

      let postedPayrolls = Array.isArray(payrollResponse) 
        ? payrollResponse.filter(p => p.payroll_IsPosted === true)
        : [];

      if (payrollId) {
        postedPayrolls = postedPayrolls.filter(p => p.payroll_Id === payrollId);
      }

      const historyData = [];

      // For each posted payroll, get the payroll details and calculate summary
      for (const payroll of postedPayrolls) {
        try {
          const details = await getPayrollDetailHistory({
            payroll_Id: payroll.payroll_Id,
            employee_Id: null
          });

          const detailsArray = Array.isArray(details) ? details : [];
          
          if (detailsArray.length > 0) {
            const netSalaryTotal = detailsArray.reduce((sum, detail) => sum + (detail.net_Salary || 0), 0);
            const employeeCount = detailsArray.length;
            const otherAdditionsTotal = detailsArray.reduce((sum, detail) => sum + (detail.other_Additions || 0), 0);
            const otherDeductionsTotal = detailsArray.reduce((sum, detail) => sum + (detail.other_Deductions || 0), 0);

            historyData.push({
              payroll_Id: payroll.payroll_Id,
              payroll_Date: payroll.payroll_Date,
              payroll_PostedDate: payroll.payroll_PostedDate,
              netSalaryTotal,
              employeeCount,
              otherAdditionsTotal,
              otherDeductionsTotal
            });
          }
        } catch (detailError) {
          console.error(`Error loading details for payroll ${payroll.payroll_Id}:`, detailError);
        }
      }

      setPayrollHistory(historyData);
      setTotalCount(historyData.length);
    } catch (error) {
      console.error('Error loading payroll history:', error);
      Swal.fire("Error", "Failed to load payroll history: " + error.message, "error");
      setPayrollHistory([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    const payrollId = selectedPayroll ? selectedPayroll.value : null;
    setPage(1); // Reset to first page when applying filter
    loadPayrollHistory(payrollId);
  };

  const handleClearFilter = () => {
    setSelectedPayroll(null);
    setPage(1);
    loadPayrollHistory();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const filterData = {
        payroll_Id: selectedPayroll ? selectedPayroll.value : null
      };
      
      await exportPayrollHistoryToExcel(filterData);
      Swal.fire("Success", "Payroll history report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export payroll history report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedHistory = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return payrollHistory.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD/MM/YYYY");
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toFixed(2);
  };

  if (!hasPermission) {
    return <div>{t('No permission to view this page')}</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title={t("Payroll History Report")} pageTitle={t("Reports")} />
        
        <Card>
          <CardHeader className="d-flex align-items-center">
            <h4 className="card-title mb-0 flex-grow-1">{t("Payroll History Report")}</h4>
            <Button
              color="success"
              onClick={handleExportExcel}
              disabled={loading}
              className="ms-2"
            >
              <Download className="me-1" size={16} />
              {t("Export Excel")}
            </Button>
          </CardHeader>
          <CardBody>
            {/* Filter Section */}
            <Row className="mb-4">
              <Col md={4}>
                <FormGroup>
                  <Label>{t("Payroll Month")}</Label>
                  <Select
                    value={selectedPayroll}
                    onChange={setSelectedPayroll}
                    options={payrollList}
                    placeholder={t("Select Payroll Month")}
                    className="select2-selection"
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    onClick={handleApplyFilter}
                    disabled={loading}
                  >
                    {loading ? t("Loading...") : t("Apply")}
                  </Button>
                  <Button 
                    color="warning" 
                    onClick={handleClearFilter}
                    disabled={loading}
                  >
                    {t("Clear")}
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Table Section */}
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      {t("Payroll History")} ({totalCount} {t("records")})
                    </h5>
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">{t("Loading...")}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table bordered>
                          <thead className="table-light">
                            <tr>
                              <th>{t("Sr.No")}</th>
                              <th>{t("Payroll Month")}</th>
                              <th>{t("Posted Date")}</th>
                              <th>{t("Net Salary Total")}</th>
                              <th>{t("Employees")}</th>
                              <th>{t("Other Additions Total")}</th>
                              <th>{t("Other Deductions Total")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payrollHistory.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center">
                                  {t("No payroll history found")}
                                </td>
                              </tr>
                            ) : (
                              getPaginatedHistory().map((item, index) => (
                                <tr key={item.payroll_Id}>
                                  <td>{(page - 1) * pageSize + index + 1}</td>
                                  <td>{moment(item.payroll_Date).format("MMMM YYYY")}</td>
                                  <td>{formatDate(item.payroll_PostedDate)}</td>
                                  <td className="text-end">{formatCurrency(item.netSalaryTotal)}</td>
                                  <td className="text-center">{item.employeeCount}</td>
                                  <td className="text-end">{formatCurrency(item.otherAdditionsTotal)}</td>
                                  <td className="text-end">{formatCurrency(item.otherDeductionsTotal)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Pagination */}
                    {totalCount > pageSize && (
                      <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                           style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                        <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                          <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                          {t("Showing")} <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> {t("to")} <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> {t("of")} <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> {t("entries")}
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PayrollHistoryReportPage; 